import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';
import useTranslation from '../../../hooks/useTranslation';
import useEscapeKey from '../../../hooks/useEscapeKey';

/**
 * CostCodeNoteModal
 * Opens a small modal to view/edit the estimation note for a single cost code row.
 * Saves to project_costs.estimation_note, note_updated_at, note_updated_by.
 */
const CostCodeNoteModal = ({ costCodeId, costCode, description, onClose, onSaved }) => {
  const { t } = useTranslation();
  const [noteText, setNoteText] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const textareaRef = useRef(null);

  useEscapeKey(() => { onClose(); }, !saving);

  // Fetch current note on open
  useEffect(() => {
    const fetchNote = async () => {
      try {
        setLoading(true);
        const { data, error: fetchError } = await supabase?.from('project_costs')?.select('estimation_note')?.eq('id', costCodeId)?.single();

        if (fetchError) throw fetchError;
        setNoteText(data?.estimation_note || '');
      } catch (err) {
        console.error('Error fetching estimation note:', err);
        setError(t('costCodeNote.errors.load'));
      } finally {
        setLoading(false);
        // Autofocus textarea after load
        setTimeout(() => textareaRef?.current?.focus(), 50);
      }
    };

    if (costCodeId) fetchNote();
  }, [costCodeId]);

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');

      const trimmed = String(noteText ?? '')?.trim();
      const { data: { user } } = await supabase?.auth?.getUser();

      const { error: updateError } = await supabase?.from('project_costs')?.update({
          estimation_note: trimmed?.length > 0 ? trimmed : null,
          note_updated_at: new Date()?.toISOString(),
          note_updated_by: user?.id || null,
        })?.eq('id', costCodeId);

      if (updateError) throw updateError;

      // Notify parent with the saved note value (trimmed, or null if empty)
      onSaved?.(trimmed?.length > 0 ? trimmed : null);
      onClose();
    } catch (err) {
      console.error('Error saving estimation note:', err);
      setError(t('costCodeNote.errors.save'));
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (e) => {
    // Ctrl+Enter or Cmd+Enter to save
    if ((e?.ctrlKey || e?.metaKey) && e?.key === 'Enter') {
      e?.preventDefault();
      handleSave();
    }
    // Escape to close
    if (e?.key === 'Escape') {
      onClose();
    }
  };

  const title = `${t('costCodeNote.title')} — ${costCode || ''} ${description || ''}`?.trim();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      {/* Modal */}
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md flex flex-col max-h-[90vh] overflow-hidden" role="dialog" aria-modal="true">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="flex-shrink-0 w-9 h-9 rounded-xl bg-amber-50 flex items-center justify-center">
              <Icon name="Calculator" size={18} className="text-amber-600" />
            </div>
            <div className="min-w-0">
              <h2 className="text-sm font-semibold text-gray-900 leading-snug truncate" title={title}>
                {title}
              </h2>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors flex-shrink-0"
          >
            <Icon name="X" size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {error && (
            <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mb-3">
              {error}
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="w-5 h-5 border-2 border-gray-200 border-t-primary rounded-full animate-spin" />
            </div>
          ) : (
            <textarea
              ref={textareaRef}
              value={noteText}
              onChange={(e) => setNoteText(e?.target?.value)}
              onKeyDown={handleKeyDown}
              placeholder={t('costCodeNote.placeholder')}
              rows={7}
              className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary resize-none"
            />
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-gray-100 flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-3.5 py-1.5 text-xs font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
          >
            {t('costCodeNote.cancel')}
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || loading}
            className="inline-flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-medium text-white bg-primary hover:bg-primary-dark rounded-lg transition-colors disabled:opacity-60"
          >
            <Icon name="Check" size={13} />
            {saving ? t('costCodeNote.saving') : t('costCodeNote.save')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CostCodeNoteModal;
